import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Cookies from 'universal-cookie';
import { FORMATERS } from '@BICE_Vida/scp-components';

import { Button, Text } from '@BICE_Vida/components';

import routes from '@/utils/routes';
import Card from '@/designSystem/Card';

import {
  summaryStepTexts,
  quoterStepStexts,
  comparePlansTexts,
  coveragesStepTexts,
  hiringTabsItems,
} from '@/utils/generalTexts';
import componentsId from '@/utils/componentsId';
import { formatMoney, formatUF } from '@/utils/format';
import { extractUFfromBold } from '@/utils/comparePlans';
import { getPlanByType } from '@/utils/estimationFunctions';
import { AvailablePlan } from '@/src/types/availablePlans';
import {
  CAT_SUCCESS_VARIABLE,
  CONTENT_COPY_BLUE,
  NOTEBOOK,
  WHITE_HEADPHONES_ANGLE,
  WRITING_MESSAGE,
} from '@/utils/assets';
import { TypeCoverage } from '@/utils/constants';

import {
  clickActionsEvents,
  quotationFlowEvents,
  trackPageViewQuotation,
} from '@/utils/dataLayer/quotationFlow';

import * as planSelector from '@/redux/plans/selectors';
import * as wizardSelector from '@/redux/wizard/selectors';
import * as ufSelectors from '@/redux/uf/selectors';
import * as hiringSelectors from '@/redux/hiring/selectors';

import Toaster from '@/designSystem/Toaster';
import WizardStepper from '../Wizard/WizardStepper';
import AvailablePlanModal from '../AvailablePlanModal';
import Badge from '../Badge';

import { UF as UFType } from '../../types/uf';

import styles from './QuotationSummaryStep.module.scss';

const QuotationSummaryStep = () => {
  const router = useRouter();
  const cookies = new Cookies();

  const leadCreated = useSelector(hiringSelectors.getLeadCreated);
  const emailUser = useSelector(hiringSelectors.getEmailHiring);
  const clientToken = cookies.get('x-amzn-client');

  const prePlanSelected = useSelector(planSelector.getPreSelectedPlan());
  const availablePlans: AvailablePlan[] = useSelector(planSelector.getAvailablePlans);
  const workersSelected = useSelector(wizardSelector.getWorkersSelected);
  const variantSelected = useSelector(wizardSelector.getVariantSelected);
  const coverageSelected = useSelector(wizardSelector.getSelectedCoverages);
  const getDataLayerInfo = useSelector(wizardSelector.getDataLayerInfo);
  const branding = useSelector(wizardSelector.getBranding);

  const totalUFCompany = useSelector(wizardSelector.getTotalCompany);
  const totalUFByPlan = useSelector(wizardSelector.getTotalByPlan);
  const totalUFVariants = useSelector(wizardSelector.getTotalVariants);

  const UF: UFType = useSelector(ufSelectors.getUF);
  const today = UF.date && UF.date.split('-').reverse().join('/');

  const dentalPlan = prePlanSelected
    ? getPlanByType(availablePlans, prePlanSelected, TypeCoverage.dental)
    : undefined;

  const catastrophicPlan = prePlanSelected
    ? getPlanByType(availablePlans, prePlanSelected, TypeCoverage.catastrophic)
    : undefined;

  const deceasePlan = prePlanSelected
    ? getPlanByType(availablePlans, prePlanSelected, TypeCoverage.decease)
    : undefined;

  const [showUF, setShowUF] = useState(false);
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const [showToaster, setShowToaster] = useState(false);

  useEffect(() => {
    trackPageViewQuotation({
      eventParams: {
        ...quotationFlowEvents.summaryStep,
        ...getDataLayerInfo.event_params,
        attribution: branding.code,
      },
      user: getDataLayerInfo.user,
    });
  }, []);

  const copyEmailToClipboard = async () => {
    await navigator.clipboard.writeText('segurospyme@bicevida.cl');
    setShowToaster(true);
    trackPageViewQuotation({
      eventParams: {
        ...quotationFlowEvents.summaryStep,
        ...getDataLayerInfo.event_params,
        action_type: 'click',
        action: clickActionsEvents.summaryStepCopyEmail,
      },
      user: getDataLayerInfo.user,
    });
  };
  const openPlanDetailsModal = () => {
    setShowPlanDetails(true);
    trackPageViewQuotation({
      eventParams: {
        ...quotationFlowEvents.summaryStep,
        ...getDataLayerInfo.event_params,
        action_type: 'click',
        action: clickActionsEvents.summaryStepViewCoverage,
      },
      user: getDataLayerInfo.user,
    });
  };
  const closePlanDetailsModal = () => setShowPlanDetails(false);

  const coveragesArray = useMemo(
    () =>
      [
        {
          id: TypeCoverage.dental,
          title: coveragesStepTexts.dental.title,
          totalCLP: FORMATERS.formatCLP(
            (dentalPlan?.cost || 0) * UF.value * (workersSelected || 0)
          ),
          totalUF: formatUF((dentalPlan?.cost || 0) * (workersSelected || 0)),
          checked: coverageSelected.dental || false,
          active: !!dentalPlan?.plan,
        },
        {
          id: TypeCoverage.catastrophic,
          title: coveragesStepTexts.catastrophic.title,
          totalCLP: FORMATERS.formatCLP(
            (catastrophicPlan?.cost || 0) * UF.value * (workersSelected || 0)
          ),
          totalUF: formatUF((catastrophicPlan?.cost || 0) * (workersSelected || 0)),
          checked: coverageSelected.catastrophic || false,
          active: !!catastrophicPlan?.plan,
        },
        {
          id: TypeCoverage.decease,
          title: coveragesStepTexts.decease.title,
          totalCLP: FORMATERS.formatCLP(
            (deceasePlan?.cost || 0) * UF.value * (workersSelected || 0)
          ),
          totalUF: formatUF((deceasePlan?.cost || 0) * (workersSelected || 0)),
          checked: coverageSelected.decease || false,
          active: !!deceasePlan?.plan,
        },
      ].filter((coverage) => coverage.active),
    [catastrophicPlan, dentalPlan, deceasePlan, coverageSelected, UF.value, workersSelected]
  );

  const handleHireButton = () => {
    trackPageViewQuotation({
      eventParams: {
        ...quotationFlowEvents.summaryStep,
        ...getDataLayerInfo.event_params,
        action_type: 'click',
        action: clickActionsEvents.summaryStepHireOnline,
      },
      user: getDataLayerInfo.user,
    });
    router.push({
      pathname: routes.hiringDashboard,
      query: {
        lead: leadCreated,
        client: clientToken,
        tab: hiringTabsItems[0].id,
      },
    });
  };

  const handleBackToLanding = () => {
    trackPageViewQuotation({
      eventParams: {
        ...quotationFlowEvents.summaryStep,
        ...getDataLayerInfo.event_params,
        action_type: 'click',
        action: clickActionsEvents.summaryStepBackToLanding,
      },
      user: getDataLayerInfo.user,
    });
    router.push(routes.landing);
  };

  const handleToggleUF = (checked: boolean) => {
    setShowUF(checked);
  };

  return (
    <div className={styles.summary_container}>
      <AvailablePlanModal
        isOpen={showPlanDetails}
        handleClose={closePlanDetailsModal}
        plan={prePlanSelected}
        coverages={prePlanSelected?.coverage}
        UF={UF}
      />

      <div className={styles.summary_header}>
        <Image src={CAT_SUCCESS_VARIABLE} alt="cotización enviada" width={117} height={80} />
        <Text className={styles.summary_title} fontTypo="display-small">
          {summaryStepTexts.title}
        </Text>
        {emailUser && (
          <Text className={styles.summary_subtitle} fontTypo="body-medium">
            Te enviamos los detalles a tu correo <strong>{emailUser}</strong>.
          </Text>
        )}
      </div>

      <div className={styles.stepper_container}>
        <WizardStepper />
      </div>

      <div className={styles.summary_body}>
        {/* Left Column - Resumen de tu cotización */}
        <div className={styles.summary_left}>
          <label className={styles.switch_container} htmlFor="toggle-uf">
            <input
              id="toggle-uf"
              type="checkbox"
              checked={showUF}
              onChange={(e) => handleToggleUF(e.target.checked)}
              className={styles.switch_input}
            />
            <span className={styles.switch_slider} />
            <span className={styles.switch_label}>Valor en UF</span>
          </label>

          <div className={styles.reviewer_container}>
            <div className={styles.reviewer_section}>
              <div className={styles.reviewer_title}>{quoterStepStexts.reviewTitle}</div>
            </div>

            {prePlanSelected && (
              <div className={styles.reviewer_section}>
                <Badge
                  text="Plan seleccionado"
                  color="F5F7FA"
                  textColor="1F61F7"
                  variant="colorized"
                  iconPosition="none"
                  size="small"
                  customClass={styles.reviewer_badge}
                />

                <div className={styles.reviewer_content_plan}>
                  <div className={styles.reviewer_content}>
                    <div className={styles.reviewer_plan_name}>{prePlanSelected.description}</div>
                    <div className={styles.reviewer_plan_detail}>
                      {comparePlansTexts.subtitle(
                        prePlanSelected.coverage.health.value,
                        extractUFfromBold(prePlanSelected.cardCoverages[3])
                      )}
                    </div>
                    <div className={styles.reviewer_plan_price_line}>
                      <span className={styles.reviewer_plan_price}>
                        {showUF
                          ? formatUF(prePlanSelected.price.uf)
                          : FORMATERS.formatCLP(prePlanSelected.price.uf * UF.value)}
                      </span>{' '}
                      <span className={styles.reviewer_plan_detail}>por trabajador</span>
                    </div>
                  </div>
                  <Button
                    variant="text"
                    text="Ver cobertura"
                    size="medium"
                    onClick={openPlanDetailsModal}
                    className={styles.reviewer_button_link}
                  />
                </div>
              </div>
            )}

            {workersSelected > 0 && (
              <div className={styles.reviewer_section}>
                <div className={styles.reviewer_content}>
                  <div className={styles.reviewer_workers_title}>
                    {quoterStepStexts.reviewWorkers}
                  </div>
                  <div className={styles.reviewer_workers_count}>
                    {workersSelected} trabajadores
                  </div>
                </div>
              </div>
            )}

            {coveragesArray.some((coverage) => coverage.checked) && (
              <div className={styles.reviewer_section}>
                <div className={styles.reviewer_content}>
                  <div className={styles.reviewer_content_title}>
                    {quoterStepStexts.reviewCoverages}
                    <span className={styles.reviewer_content_title_suffix}> por trabajador</span>
                  </div>
                  <div className={styles.reviewer_content_coverages}>
                    {coveragesArray
                      .filter((coverage) => coverage.checked)
                      .map((coverage, index) => (
                        <div
                          key={`${coverage.title}-${index}`}
                          className={styles.reviewer_coverage_row}
                        >
                          <div>{coverage.title}</div>
                          <div className={styles.reviewer_plan_price_line}>
                            <span className={styles.reviewer_plan_price_plus}>
                              {showUF ? coverage.totalUF : coverage.totalCLP}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {variantSelected.distributed && (
              <div className={styles.reviewer_section}>
                <div className={styles.reviewer_content}>
                  <div className={styles.reviewer_content_title}>
                    {quoterStepStexts.reviewVariants}
                  </div>
                  <div className={styles.reviewer_coverage_row}>
                    <div>Total cargas</div>
                    <div className={styles.reviewer_plan_price_line}>
                      <span className={styles.reviewer_plan_price_plus}>
                        {showUF
                          ? formatUF(totalUFVariants)
                          : formatMoney(totalUFVariants * UF.value)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className={styles.reviewer_summary}>
              <span>Total mensual</span>
              <div>
                <span>
                  {showUF ? formatUF(totalUFCompany) : formatMoney(totalUFCompany * UF.value)}
                </span>
              </div>
            </div>
          </div>

          <div className={styles.uf_warning}>
            {quoterStepStexts.reviewUfAlert(UF.value, today as string)}
          </div>
        </div>

        <div className={styles.summary_right}>
          <Text className={styles.actions_title} fontTypo="title-medium">
            {summaryStepTexts.whatCanYouDoTitle}
          </Text>
          <Card customClass={styles.actions_card} bordered>
            <div>
              <div className={styles.action_item}>
                <div className={styles.action_icon}>
                  <Image src={NOTEBOOK} alt="contratar" width={40} height={40} />
                </div>
                <div className={styles.action_content}>
                  <Text className={styles.action_item_title} fontTypo="title-small">
                    {summaryStepTexts.hireOnlineTitle}
                  </Text>
                  <Text className={styles.action_item_description} fontTypo="body-small">
                    {summaryStepTexts.hireOnlineDescription}
                  </Text>
                </div>
              </div>

              <div className={styles.action_item}>
                <div className={styles.action_icon}>
                  <Image src={WRITING_MESSAGE} alt="centro de ayuda" width={40} height={40} />
                </div>
                <div className={styles.action_content}>
                  <Text className={styles.action_item_title} fontTypo="title-small">
                    {summaryStepTexts.helpCenterTitle}
                  </Text>
                  <Text className={styles.action_item_description} fontTypo="body-small">
                    {summaryStepTexts.helpCenterDescription}{' '}
                    <a href={routes.helpDesk} className={styles.action_link}>
                      {summaryStepTexts.helpCenterLink}
                    </a>
                  </Text>
                </div>
              </div>

              <div className={styles.action_item}>
                <div className={styles.action_icon}>
                  <Image
                    src={WHITE_HEADPHONES_ANGLE}
                    alt="centro de ayuda"
                    width={40}
                    height={40}
                  />
                </div>
                <div className={styles.action_content}>
                  <Text className={styles.action_item_title} fontTypo="title-small">
                    {summaryStepTexts.contactTitle}
                  </Text>
                  <Text className={styles.action_item_description} fontTypo="body-small">
                    {summaryStepTexts.contactDescription}{' '}
                    <span className={styles.action_link_copy} onClick={copyEmailToClipboard}>
                      {summaryStepTexts.contactEmail}
                      <Image
                        src={CONTENT_COPY_BLUE}
                        alt="ícono copiar"
                        height={14}
                        width={14}
                        className={styles.content_copy_icon}
                      />
                    </span>{' '}
                    {summaryStepTexts.contactSuffix}
                  </Text>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div className={styles.summary_buttons}>
        <Button
          id={componentsId.summaryStep.btnBack}
          text={summaryStepTexts.btnBackToLanding}
          size="large"
          variant="text"
          onClick={handleBackToLanding}
        />
        <Button
          id={componentsId.summaryStep.btnHiring}
          text={summaryStepTexts.btnHiring()}
          size="large"
          onClick={handleHireButton}
        />
      </div>

      <Toaster
        setHideToaster={() => setShowToaster(false)}
        text="Correo copiado"
        show={showToaster}
        customClass={styles.toast}
        icon="check_circle"
        iconSuccess
      />
    </div>
  );
};

export default QuotationSummaryStep;
